import { useState, useEffect, useMemo, useRef } from "react";
import { Plus, Search, X, Anchor, Loader2, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * EncounterPicker — small dropdown that lets a Cruising Log entry link to
 * Cruiser Contacts (boats met on the water). Supports search of the existing
 * library and quick-create of a brand-new contact (boat name only — full
 * details can be filled later in the Contacts page).
 *
 * Props:
 *   value: string[] (selected contact ids)
 *   onChange: (newIds: string[]) => void
 *   "data-testid": optional override
 */
export default function EncounterPicker({ value = [], onChange, "data-testid": testId = "encounter-picker" }) {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState("");
    const [err, setErr] = useState("");
    const popoverRef = useRef(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await api.get("/cruiser-contacts");
                if (alive) setContacts(res.data || []);
            } catch (e) {
                if (alive) setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Couldn't load contacts.");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const onClick = (e) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) {
                setOpen(false);
                setCreating(false);
                setSearch("");
                setNewName("");
            }
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    const byId = useMemo(() => Object.fromEntries(contacts.map((c) => [c.id, c])), [contacts]);

    const selected = value.map((id) => byId[id]).filter(Boolean);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const base = contacts.filter((c) => !value.includes(c.id));
        if (!q) return base.slice(0, 25);
        return base.filter((c) => {
            const hay = [c.boat_name, c.boat_type, ...(c.people || []).map((p) => p.name)]
                .filter(Boolean).join(" ").toLowerCase();
            return hay.includes(q);
        }).slice(0, 25);
    }, [contacts, value, search]);

    const addExisting = (c) => {
        if (value.includes(c.id)) return;
        onChange([...value, c.id]);
        setSearch("");
    };

    const removeId = (id) => onChange(value.filter((v) => v !== id));

    const createAndAdd = async () => {
        const name = newName.trim();
        if (!name) return;
        setCreating(true);
        setErr("");
        try {
            const res = await api.post("/cruiser-contacts", { boat_name: name });
            const fresh = res.data;
            setContacts((prev) => [fresh, ...prev]);
            onChange([...value, fresh.id]);
            setNewName("");
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Couldn't create contact.");
        } finally {
            setCreating(false);
        }
    };

    return (
        <div data-testid={testId} style={{ position: "relative" }}>
            {/* Selected chips */}
            {selected.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                    {selected.map((c) => (
                        <span
                            key={c.id}
                            data-testid={`encounter-chip-${c.id}`}
                            style={{
                                display: "inline-flex", alignItems: "center", gap: 6,
                                padding: "5px 8px 5px 10px",
                                borderRadius: 999,
                                background: "rgba(200, 162, 92, 0.12)",
                                border: "1px solid var(--bm-gold-dim)",
                                fontSize: 12,
                                color: "var(--bm-text)",
                            }}
                        >
                            <Anchor size={11} style={{ color: "var(--bm-gold)" }} />
                            {c.boat_name}
                            <button
                                type="button"
                                onClick={() => removeId(c.id)}
                                data-testid={`encounter-chip-remove-${c.id}`}
                                aria-label={`Remove ${c.boat_name}`}
                                style={{
                                    background: "transparent", border: "none", cursor: "pointer",
                                    color: "var(--bm-text-dim)", padding: "0 0 0 2px", display: "flex",
                                }}
                            >
                                <X size={11} />
                            </button>
                        </span>
                    ))}
                </div>
            )}

            {/* Trigger */}
            <button
                type="button"
                data-testid="encounter-add-btn"
                onClick={() => setOpen((v) => !v)}
                className="bm-btn bm-btn-ghost"
                style={{ fontSize: 13, padding: "8px 12px" }}
            >
                <Plus size={13} /> {selected.length === 0 ? "Add encounter" : "Add another"}
            </button>

            {open && (
                <div
                    ref={popoverRef}
                    data-testid="encounter-popover"
                    style={{
                        position: "absolute", top: "calc(100% + 6px)", left: 0,
                        zIndex: 200, width: "min(360px, 100%)",
                        background: "var(--bm-bg-2, #111e30)",
                        border: "1px solid var(--bm-gold-dim)",
                        borderRadius: 10,
                        boxShadow: "0 12px 36px -10px rgba(0,0,0,0.55)",
                        padding: 12,
                    }}
                >
                    {/* Search */}
                    <div style={{ position: "relative", marginBottom: 10 }}>
                        <Search size={13} style={{
                            position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
                            color: "var(--bm-text-dim)", pointerEvents: "none",
                        }} />
                        <input
                            data-testid="encounter-search-input"
                            className="bm-input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search boat or person…"
                            style={{ paddingLeft: 32, fontSize: 13 }}
                            autoFocus
                        />
                    </div>

                    {/* Results */}
                    <div style={{ maxHeight: 220, overflowY: "auto", marginBottom: 10 }}>
                        {loading && (
                            <div style={{ padding: 14, textAlign: "center", color: "var(--bm-text-muted)", fontSize: 13 }}>
                                <Loader2 size={14} className="bm-spin" /> Loading…
                            </div>
                        )}
                        {!loading && filtered.length === 0 && search && (
                            <p style={{ margin: "8px 0", color: "var(--bm-text-muted)", fontSize: 12 }}>
                                No matches for "{search}".
                            </p>
                        )}
                        {!loading && filtered.length === 0 && !search && contacts.length === 0 && (
                            <p style={{ margin: "8px 0", color: "var(--bm-text-muted)", fontSize: 12 }}>
                                No contacts yet. Add your first one below ↓
                            </p>
                        )}
                        {!loading && filtered.map((c) => (
                            <button
                                key={c.id}
                                type="button"
                                data-testid={`encounter-result-${c.id}`}
                                onClick={() => addExisting(c)}
                                className="bm-btn bm-btn-ghost"
                                style={{
                                    display: "flex", alignItems: "flex-start", gap: 8,
                                    width: "100%", textAlign: "left", padding: "8px 10px",
                                    marginBottom: 4, fontSize: 13, fontWeight: 400,
                                    whiteSpace: "normal",
                                }}
                            >
                                <Anchor size={12} style={{ color: "var(--bm-gold)", marginTop: 3, flexShrink: 0 }} />
                                <span style={{ flex: 1 }}>
                                    <span style={{ display: "block", color: "var(--bm-text)" }}>{c.boat_name}</span>
                                    {(c.boat_type || (c.people || []).length > 0) && (
                                        <span style={{ display: "block", color: "var(--bm-text-dim)", fontSize: 11, marginTop: 2 }}>
                                            {[c.boat_type, (c.people || []).map((p) => p.name).filter(Boolean).join(" & ")]
                                                .filter(Boolean).join(" · ")}
                                        </span>
                                    )}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Quick create */}
                    <div style={{ borderTop: "1px solid var(--bm-border-soft)", paddingTop: 10 }}>
                        <label className="bm-label" style={{ marginBottom: 6 }}>Quick add new boat</label>
                        <div style={{ display: "flex", gap: 6 }}>
                            <input
                                data-testid="encounter-quick-create-input"
                                className="bm-input"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                placeholder="Boat name (e.g. Shamrock)"
                                style={{ flex: 1, fontSize: 13 }}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createAndAdd(); } }}
                            />
                            <button
                                type="button"
                                data-testid="encounter-quick-create-btn"
                                onClick={createAndAdd}
                                disabled={!newName.trim() || creating}
                                className="bm-btn bm-btn-gold"
                                style={{ padding: "0 14px", fontSize: 12 }}
                            >
                                {creating ? <Loader2 size={12} className="bm-spin" /> : <Check size={12} />} Add
                            </button>
                        </div>
                        <p style={{ margin: "6px 0 0", fontSize: 11, color: "var(--bm-text-dim)" }}>
                            You can fill in crew, pets &amp; contact info later in the Contacts page.
                        </p>
                    </div>

                    {err && (
                        <p data-testid="encounter-picker-error" style={{ margin: "8px 0 0", color: "#e76f51", fontSize: 12 }}>
                            {err}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
